import React, { useState } from 'react';
import { FiMapPin, FiTruck, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { calcularFreteApi } from '../../Api';

const UFS = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB',
    'PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

// Etapa de ENTREGA do checkout: pede o CEP, calcula o frete (PAC/SEDEX/Mini),
// coleta o ENDEREÇO COMPLETO (ViaCEP preenche rua/bairro/cidade/uf) e deixa o
// cliente escolher a modalidade. Coletar tudo aqui (antes de pagar) faz a
// carteirinha física já nascer pronta pra postar.
//
// onConfirmar({ cepDestino, freteValor, valorTotal, codigoServico, modalidadeNome,
//               endereco: { cep, logradouro, numero, complemento, bairro, cidade, uf } })
export default function FreteCalculator({
    produtoNome,
    produtoValor,
    loading = false,
    onConfirmar,
    onResetar,
}) {
    const [cep, setCep] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [uf, setUf] = useState('');

    const [calculando, setCalculando] = useState(false);
    const [opcoes, setOpcoes] = useState(null);
    const [selecionado, setSelecionado] = useState(null);
    const [erro, setErro] = useState(null);

    function maskCep(v) {
        const d = String(v || '').replace(/\D/g, '').slice(0, 8);
        return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    }

    async function handleCalcular() {
        const limpo = cep.replace(/\D/g, '');
        if (limpo.length !== 8) {
            setErro('CEP inválido — precisa 8 dígitos.');
            return;
        }
        setErro(null);
        setCalculando(true);
        try {
            // ViaCEP (preenche endereço) + frete em paralelo.
            const [resFrete, viacep] = await Promise.all([
                calcularFreteApi({ cepDestino: limpo }),
                fetch(`https://viacep.com.br/ws/${limpo}/json/`).then(r => r.json()).catch(() => null),
            ]);

            const lista = (resFrete.data?.opcoes || []).map(o => ({
                codigo: o.codigo, nome: o.nome,
                valor: Number(o.valor) || 0, prazoUtilDias: o.prazoUtilDias || null,
            }));
            if (lista.length === 0) {
                setErro('Nenhuma modalidade de frete disponível para esse CEP.');
                setOpcoes(null);
                return;
            }

            if (viacep && !viacep.erro) {
                setLogradouro(viacep.logradouro || '');
                setBairro(viacep.bairro || '');
                setCidade(viacep.localidade || '');
                setUf(viacep.uf || '');
            }

            setOpcoes(lista);
            const maisBarata = lista.reduce((a, b) => (b.valor < a.valor ? b : a), lista[0]);
            setSelecionado(maisBarata.codigo);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.details
                || err?.message || 'Falha ao calcular frete.';
            setErro(msg);
            setOpcoes(null);
        } finally {
            setCalculando(false);
        }
    }

    function handleConfirmar() {
        if (!opcoes || !selecionado) return;
        const opcao = opcoes.find(o => o.codigo === selecionado);
        if (!opcao) return;
        if (!logradouro.trim() || !numero.trim() || !bairro.trim() || !cidade.trim() || !uf) {
            setErro('Preencha o endereço completo (rua, número, bairro, cidade e UF).');
            return;
        }
        setErro(null);
        onConfirmar({
            cepDestino: cep.replace(/\D/g, ''),
            freteValor: opcao.valor,
            valorTotal: produtoValor + opcao.valor,
            codigoServico: opcao.codigo,
            modalidadeNome: opcao.nome,
            endereco: {
                cep: cep.replace(/\D/g, ''),
                logradouro: logradouro.trim(),
                numero: numero.trim(),
                complemento: complemento.trim(),
                bairro: bairro.trim(),
                cidade: cidade.trim(),
                uf,
            },
        });
    }

    function handleTrocarCep() {
        setOpcoes(null);
        setSelecionado(null);
        setErro(null);
        if (onResetar) onResetar();
    }

    const opcaoSelecionada = opcoes?.find(o => o.codigo === selecionado) || null;
    const valorTotal = opcaoSelecionada ? produtoValor + opcaoSelecionada.valor : produtoValor;
    const desabilitado = loading || calculando;

    return (
        <div style={styles.wrap}>
            <div style={styles.header}>
                <FiTruck size={18} /> <span>Endereço de entrega</span>
            </div>

            {!opcoes ? (
                <>
                    <label style={styles.label}>CEP de entrega</label>
                    <div style={styles.inputRow}>
                        <div style={styles.inputWrap}>
                            <FiMapPin size={16} style={styles.icon} />
                            <input
                                type="text" inputMode="numeric" placeholder="00000-000" maxLength={9}
                                value={cep}
                                onChange={e => setCep(maskCep(e.target.value))}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !desabilitado && cep.replace(/\D/g, '').length === 8) {
                                        e.preventDefault(); handleCalcular();
                                    }
                                }}
                                disabled={desabilitado} style={styles.input}
                            />
                        </div>
                        <button
                            type="button" onClick={handleCalcular}
                            disabled={desabilitado || cep.replace(/\D/g, '').length !== 8}
                            style={{ ...styles.btnPrimary, opacity: (desabilitado || cep.replace(/\D/g, '').length !== 8) ? 0.6 : 1 }}
                        >
                            {calculando ? 'Calculando…' : 'Calcular frete'}
                        </button>
                    </div>
                    {erro && <div style={styles.erro}>{erro}</div>}
                    <p style={styles.hint}>Informe o CEP pra calcular o frete e preencher o endereço.</p>
                </>
            ) : (
                <>
                    {/* Endereço (ViaCEP preenche o que dá; número é obrigatório) */}
                    <div style={styles.cepLinha}>
                        <span><b>CEP:</b> {cep}</span>
                        <button type="button" onClick={handleTrocarCep} disabled={desabilitado} style={styles.btnTrocar}>
                            <FiRefreshCw size={12} /> Trocar
                        </button>
                    </div>

                    <label style={styles.label}>Rua / Logradouro</label>
                    <input type="text" value={logradouro} onChange={e => setLogradouro(e.target.value)}
                        disabled={desabilitado} placeholder="Rua / Avenida" style={styles.input} />

                    <div style={styles.row2}>
                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>Número</label>
                            <input type="text" value={numero} onChange={e => setNumero(e.target.value)}
                                disabled={desabilitado} placeholder="123" style={styles.input} />
                        </div>
                        <div style={{ flex: 1.4 }}>
                            <label style={styles.label}>Complemento</label>
                            <input type="text" value={complemento} onChange={e => setComplemento(e.target.value)}
                                disabled={desabilitado} placeholder="Apto, bloco… (opcional)" style={styles.input} />
                        </div>
                    </div>

                    <label style={styles.label}>Bairro</label>
                    <input type="text" value={bairro} onChange={e => setBairro(e.target.value)}
                        disabled={desabilitado} placeholder="Centro" style={styles.input} />

                    <div style={styles.row2}>
                        <div style={{ flex: 2 }}>
                            <label style={styles.label}>Cidade</label>
                            <input type="text" value={cidade} onChange={e => setCidade(e.target.value)}
                                disabled={desabilitado} placeholder="Cidade" style={styles.input} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>UF</label>
                            <select value={uf} onChange={e => setUf(e.target.value)} disabled={desabilitado} style={styles.input}>
                                <option value="">UF</option>
                                {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Modalidade */}
                    <label style={{ ...styles.label, marginTop: '0.8rem' }}>Modalidade de envio</label>
                    <div style={styles.opcoesList}>
                        {opcoes.map(o => {
                            const ativo = o.codigo === selecionado;
                            return (
                                <button type="button" key={o.codigo} onClick={() => setSelecionado(o.codigo)}
                                    disabled={desabilitado}
                                    style={{ ...styles.opcaoItem, borderColor: ativo ? '#059669' : '#E2E8F0', background: ativo ? '#ECFDF5' : '#FFFFFF' }}>
                                    <span style={styles.radioOuter}>{ativo && <span style={styles.radioInner} />}</span>
                                    <span style={styles.opcaoInfo}>
                                        <span style={styles.opcaoNome}>{o.nome}</span>
                                        {o.prazoUtilDias && <span style={styles.opcaoPrazo}>{o.prazoUtilDias} dias úteis</span>}
                                    </span>
                                    <b style={styles.opcaoValor}>R$ {o.valor.toFixed(2).replace('.', ',')}</b>
                                </button>
                            );
                        })}
                    </div>

                    <div style={styles.resumo}>
                        <Linha label={produtoNome || 'Produto'} valor={produtoValor} />
                        <Linha label={`Frete${opcaoSelecionada ? ` — ${opcaoSelecionada.nome}` : ''}`} valor={opcaoSelecionada ? opcaoSelecionada.valor : 0} />
                        <hr style={styles.divisor} />
                        <Linha label="Total" valor={valorTotal} destacado />
                    </div>

                    {erro && <div style={styles.erro}>{erro}</div>}

                    <button type="button" onClick={handleConfirmar} disabled={desabilitado || !selecionado} style={styles.btnConfirmar}>
                        <FiCheck size={15} /> Confirmar e continuar
                    </button>
                </>
            )}
        </div>
    );
}

function Linha({ label, valor, destacado }) {
    return (
        <div style={{ ...styles.linha, ...(destacado ? styles.linhaDestacada : {}) }}>
            <span>{label}</span>
            <b>R$ {Number(valor || 0).toFixed(2).replace('.', ',')}</b>
        </div>
    );
}

const styles = {
    wrap: { background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: '1rem', marginBottom: '1rem' },
    header: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#0F172A', marginBottom: '0.75rem', fontSize: '0.95rem' },
    label: { display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', fontWeight: 600, color: '#475569', margin: '0.55rem 0 0.25rem' },
    inputRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
    inputWrap: { flex: 1, minWidth: 130, display: 'flex', alignItems: 'center', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10, padding: '0 0.5rem' },
    icon: { color: '#94A3B8', marginRight: 6 },
    input: {
        width: '100%', border: '1px solid #E2E8F0', borderRadius: 10, padding: '0.6rem 0.7rem',
        fontSize: '0.9rem', color: '#0F172A', fontFamily: 'inherit', background: '#FFFFFF',
        boxSizing: 'border-box', outline: 'none',
    },
    row2: { display: 'flex', gap: '0.5rem' },
    cepLinha: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: '#334155', marginBottom: 4 },
    btnTrocar: {
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 999,
        border: '1px solid #CBD5E1', background: '#fff', color: '#475569', fontSize: '0.76rem',
        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    },
    btnPrimary: {
        padding: '0.7rem 1rem', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', color: '#FFFFFF',
        fontWeight: 700, fontSize: '0.88rem', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
    },
    erro: { marginTop: '0.5rem', color: '#9F1239', fontSize: '0.82rem', padding: '0.5rem 0.75rem', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8 },
    hint: { fontSize: '0.75rem', color: '#64748B', margin: '0.5rem 0 0', lineHeight: 1.5 },
    opcoesList: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.85rem' },
    opcaoItem: {
        display: 'flex', alignItems: 'center', gap: 10, padding: '0.7rem 0.85rem',
        border: '1.5px solid', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
        fontFamily: 'inherit', width: '100%',
    },
    radioOuter: { width: 18, height: 18, borderRadius: '50%', border: '2px solid #059669', flexShrink: 0, display: 'grid', placeItems: 'center' },
    radioInner: { width: 9, height: 9, borderRadius: '50%', background: '#059669' },
    opcaoInfo: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
    opcaoNome: { fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' },
    opcaoPrazo: { fontSize: '0.74rem', color: '#64748B', marginTop: 1 },
    opcaoValor: { color: '#0F172A', fontSize: '0.95rem', whiteSpace: 'nowrap' },
    resumo: { background: '#FFFFFF', padding: '0.85rem', borderRadius: 10, border: '1px solid #E2E8F0' },
    linha: { display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#334155', padding: '0.25rem 0' },
    linhaDestacada: { fontSize: '1rem', color: '#0F172A', fontWeight: 700, paddingTop: '0.5rem' },
    divisor: { border: 0, borderTop: '1px solid #E2E8F0', margin: '0.5rem 0' },
    btnConfirmar: {
        width: '100%', marginTop: '0.85rem', padding: '0.8rem', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', color: '#FFFFFF', fontWeight: 700,
        fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit',
    },
};
